import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text
} from '@chakra-ui/react';
import BoundingBoxDrawer from './BoundingBoxDrawer';

export default function BoundingBoxModal({
  isOpen,
  onClose,
  onComplete,
  imageUrl,
  featureKey,
  featureLabel
}) {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [boxes, setBoxes] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setBoxes([]);
    setImageLoaded(false);
    
    if (isOpen) {
      const clearEvents = () => {
        document.body.style.pointerEvents = 'none';
        requestAnimationFrame(() => {
          document.body.style.pointerEvents = '';
        });
      };
      clearEvents();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!containerRef.current || !imageRef.current || !imageLoaded) return;

    const updateSizes = () => {
      if (!imageRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;
      const imageAspectRatio = naturalWidth / naturalHeight;
      const containerAspectRatio = containerRect.width / containerRect.height;

      let displayWidth, displayHeight;

      if (containerAspectRatio > imageAspectRatio) {
        // Container is wider than image
        displayHeight = containerRect.height;
        displayWidth = displayHeight * imageAspectRatio;
      } else {
        // Container is taller than image
        displayWidth = containerRect.width;
        displayHeight = displayWidth / imageAspectRatio;
      }

      setDisplaySize({
        width: Math.round(displayWidth),
        height: Math.round(displayHeight)
      });

      setNaturalSize({
        width: naturalWidth,
        height: naturalHeight
      });
    };

    const resizeObserver = new ResizeObserver(updateSizes);
    resizeObserver.observe(containerRef.current);

    // Initial update
    updateSizes();

    return () => resizeObserver.disconnect();
  }, [imageLoaded]);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setNaturalSize({
      width: naturalWidth,
      height: naturalHeight
    });
    setImageLoaded(true);
  };

  const handleBoxDrawn = (box) => {
    if (!box.x || !box.y || !box.width || !box.height) return;
    
    // Debug box drawn
    console.log('BoundingBoxModal - Box drawn:', box);
    
    setBoxes(prev => [...prev, box]);
  };

  const handleBoxUpdated = (updatedBox, index) => {
    // Debug box update
    console.log('BoundingBoxModal - Box updated:', { updatedBox, index });
    
    setBoxes(prev => {
      const newBoxes = [...prev];
      if (updatedBox === null) {
        newBoxes.splice(index, 1);
      } else if (updatedBox.x && updatedBox.y && updatedBox.width && updatedBox.height) {
        newBoxes[index] = updatedBox;
      }
      return newBoxes;
    });
  };

  const handleComplete = () => {
    // Only process boxes with valid coordinates
    const validBoxes = boxes.filter(box => 
      box.x != null && 
      box.y != null && 
      box.width != null && 
      box.height != null
    );

    // Debug valid boxes
    console.log('BoundingBoxModal - Valid boxes for completion:', validBoxes);

    // Convert to normalized predictions with confidence
    const normalizedBoxes = validBoxes.map(box => {
        const normalized = {
          class: featureKey,
          label: featureLabel,
          source: 'manual',
          confidence: 0.99,
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height
        };
      
      // Debug normalized box
      console.log('BoundingBoxModal - Normalized box:', normalized);
      
      return normalized;
    });

    onComplete(normalizedBoxes);
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="6xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>Draw Bounding Boxes for {featureLabel}</ModalHeader>
        <ModalBody p={0}>
          <Text p={4}>
            Click and drag on the image to draw bounding boxes around the feature.
            You can draw multiple boxes if needed.
          </Text>
          <Box 
            ref={containerRef}
            position="relative" 
            width="100%"
            height="calc(90vh - 200px)"
            bg="bg.default" // Use theme token
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
          >
            <Box 
              position="relative"
              width={displaySize.width}
              height={displaySize.height}
            >
              <Box
                as="img"
                ref={imageRef}
                src={imageUrl}
                alt="Draw bounding boxes"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
                onLoad={handleImageLoad}
              />
              {imageLoaded && displaySize.width > 0 && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  width="100%"
                  height="100%"
                >
                  <BoundingBoxDrawer
                    imageDisplaySize={displaySize}
                    onBoxDrawn={handleBoxDrawn}
                    onBoxUpdated={handleBoxUpdated}
                    initialBoxes={boxes}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand.primary" // Use theme token
            onClick={handleComplete}
            isDisabled={boxes.length === 0}
          >
            Save Boxes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
